import { Alert, Button, Modal } from "antd";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { DialogRef, TMSPalletDimension } from "@/types";
import PalletDimensionForm, { PalletDimensionFormRef } from "../dimension";
import { completeScanOrderPallet } from "@/apis/tms/order";
import { useGlobalContext } from "@/hooks/context";

export interface PalletCompleteDialogOpenProps {
  palletId: string;
  palletNo: string;
  orderId: string;
  printPalletLabel: boolean;
  printShippingLabel: boolean;
  requireDimension: boolean;
  completeCb: (
    orderId: string,
    palletId: string,
    toPrintPalletLabel: boolean,
    printShippingLabel: boolean
  ) => Promise<void>;
}

interface PalletCompleteConfirmDialogProps {
  className?: string;
}

const PalletCompleteConfirmDialog = forwardRef<
  DialogRef,
  PalletCompleteConfirmDialogProps
>((props, ref) => {
  const { className } = props;
  const { hintPrompt } = useGlobalContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [palletNo, setPalletNo] = useState<string | undefined>(undefined);
  const [requireDimension, setRequireDimension] = useState(false);
  const [loading, setLoading] = useState(false);

  const openPropsRef = useRef<PalletCompleteDialogOpenProps | null>(null);
  const completeCbRef = useRef<
    PalletCompleteDialogOpenProps["completeCb"] | null
  >(null);
  const dimensionsFormRef = useRef<PalletDimensionFormRef>(null);

  const closeDialog = useCallback(() => {
    setIsModalOpen(false);
    setPalletNo(undefined);
    setRequireDimension(false);
    setLoading(false);
    completeCbRef.current = null;
    openPropsRef.current = null;
  }, []);

  const onCompletePallet = useCallback(
    async (props: PalletCompleteDialogOpenProps | null) => {
      if (props == null) return;
      const {
        orderId,
        palletId,
        requireDimension,
        printPalletLabel,
        printShippingLabel,
      } = props;
      if (orderId == null || palletId == null) {
        hintPrompt("error", "Error", "Order ID or pallet ID is required");
        return;
      }
      let dimension: TMSPalletDimension | undefined;
      if (requireDimension) {
        try {
          dimension = await dimensionsFormRef.current?.getValues();
        } catch {
          hintPrompt("error", "Error", "Failed to validate pallet dimension");
          return;
        }
      }
      setLoading(true);
      completeScanOrderPallet(orderId, palletId, { dimension })
        .then(() => {
          hintPrompt("success", "Success", "Pallet completed successfully");
          return completeCbRef.current?.(
            orderId,
            palletId,
            printPalletLabel,
            printShippingLabel
          );
        })
        .then(() => {
          closeDialog();
        })
        .catch((error) => {
          hintPrompt(
            "error",
            "Error",
            `Failed to complete pallet: ${
              error.msg ?? error.message ?? "Unknown error"
            }`
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [hintPrompt, closeDialog]
  );

  const openDialog = useCallback((props: unknown) => {
    const { palletNo, requireDimension, completeCb } =
      props as PalletCompleteDialogOpenProps;
    setPalletNo(palletNo);
    setRequireDimension(requireDimension);
    completeCbRef.current = completeCb;
    openPropsRef.current = props as PalletCompleteDialogOpenProps;
    setIsModalOpen(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      showModal: openDialog,
      hideModal: closeDialog,
    }),
    [openDialog, closeDialog]
  );

  return (
    <Modal
      className={className}
      zIndex={101}
      maskClosable={false}
      footer={null}
      width={420}
      title={
        <div className="flex flex-row gap-2 items-center">
          <span className="text-lg font-bold">Complete Pallet {palletNo}</span>
        </div>
      }
      open={isModalOpen}
      onCancel={closeDialog}
      loading={loading}
    >
      <div className="flex flex-col gap-2">
        {requireDimension ? (
          <div>
            <Alert
              type="warning"
              showIcon
              message="Please enter the pallet dimension"
              className="!mb-2"
            />
            <PalletDimensionForm ref={dimensionsFormRef} />
          </div>
        ) : (
          <Alert
            className="mt-2"
            type="warning"
            showIcon
            message="Are you sure you want to complete this pallet?"
          />
        )}
      </div>
      <div className="flex flex-row gap-2 justify-end mt-4">
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          type="primary"
          onClick={() => onCompletePallet(openPropsRef.current)}
        >
          Complete Pallet
        </Button>
      </div>
    </Modal>
  );
});

PalletCompleteConfirmDialog.displayName = "PalletCompleteConfirmDialog";

export default PalletCompleteConfirmDialog;
